import { LogOut } from "lucide-react";
import { useNavigate } from "react-router";

import { InviteGate } from "@/components/auth/InviteGate";
import { useAuthStore } from "@/store/authStore";

/**
 * Shown to authenticated users who haven't completed the invite flow — most
 * often someone who signed in with Google without ever holding an invite.
 *
 * Uses the same `InviteGate` as `/signup` so the demand for a code looks the
 * same wherever it appears. There is deliberately no dismiss: this screen
 * replaces the app entirely (see `HomeOrLanding`), and the only ways past it are
 * a valid code or signing out.
 */
export function InviteLinkPrompt() {
  const { user, signOut } = useAuthStore();
  const navigate = useNavigate();

  /**
   * Accepts a bare code or any invite URL. Kept local rather than reusing
   * `extractInviteCode` because this screen must *reject* junk outright: it
   * blocks the app, so silently forwarding "not-valid!" to /join would trade a
   * clear message for a dead end.
   */
  const extractCode = (input: string): string | null => {
    const trimmed = input.trim();

    // Full URL: https://app.mash.ai/join/ABCDEF123456 or /join/CODE
    const urlMatch = trimmed.match(/\/join\/([A-Za-z0-9_-]+)/);
    if (urlMatch) return urlMatch[1];

    // Legacy URL formats
    const legacyMatch = trimmed.match(
      /\/(?:invite|app-invite)\/([A-Za-z0-9_-]+)/,
    );
    if (legacyMatch) return legacyMatch[1];

    // Raw code (alphanumeric, 8-16 chars)
    if (/^[A-Za-z0-9_-]{8,16}$/.test(trimmed)) return trimmed;

    return null;
  };

  const name = user?.displayName || user?.email;

  return (
    <InviteGate
      greeting={name ? `Welcome, ${name}` : undefined}
      subtitle="Enter the code a member shared with you to finish getting set up. The whole invite link works too."
      onSubmit={raw => {
        const code = extractCode(raw);
        if (!code) throw new Error("Please enter a valid invite code or link");
        navigate(`/join/${code}`);
      }}
      footer={
        <button
          type="button"
          onClick={() => signOut()}
          className="text-sl-500 hover:text-sl-950 inline-flex items-center gap-2 text-[15px] font-medium transition"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      }
    />
  );
}
